import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Manager,
  League,
  getManagerMe,
  listLeagues,
  createLeague,
  deleteLeague
} from '../../api/leagues';
import '../../styles/global.css';

const ManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [manager, setManager] = useState<Manager | null>(null);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create league form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newLeagueDescription, setNewLeagueDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('manager_token');
      if (!token) {
        navigate('/manager');
        return;
      }

      // Temporarily set the token for API calls
      localStorage.setItem('token', token);
      try {
        const [managerData, leaguesData] = await Promise.all([
          getManagerMe(),
          listLeagues()
        ]);
        setManager(managerData);
        setLeagues(leaguesData);
      } catch (err) {
        localStorage.removeItem('manager_token');
        navigate('/manager');
      } finally {
        localStorage.removeItem('token');
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('manager_token');
    navigate('/manager');
  };

  const handleCreateLeague = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCreating(true);

    const token = localStorage.getItem('manager_token');
    localStorage.setItem('token', token || '');

    try {
      const newLeague = await createLeague(newLeagueName, newLeagueDescription || undefined);
      setLeagues([newLeague, ...leagues]);
      setNewLeagueName('');
      setNewLeagueDescription('');
      setShowCreateForm(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create league');
    } finally {
      localStorage.removeItem('token');
      setCreating(false);
    }
  };

  const handleDeleteLeague = async (leagueId: string) => {
    if (!window.confirm('Are you sure you want to delete this league?')) {
      return;
    }

    const token = localStorage.getItem('manager_token');
    localStorage.setItem('token', token || '');

    try {
      await deleteLeague(leagueId);
      setLeagues(leagues.filter(l => l.id !== leagueId));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete league');
    } finally {
      localStorage.removeItem('token');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page">
      <nav className="navbar">
        <div className="nav-brand">League Manager</div>
        <div className="nav-links">
          <span style={{ marginRight: '15px' }}>{manager?.name}</span>
          <button className="nav-btn" onClick={() => navigate('/manager/help')}>
            Help
          </button>
          <button className="nav-btn nav-btn-secondary" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      <div className="container">
        <div className="welcome-card">
          <h1>My Leagues</h1>
          <p className="description">
            Create leagues and invite players to join
          </p>
        </div>

        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}

        <div style={{ marginBottom: '20px' }}>
          {!showCreateForm ? (
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              + Create New League
            </button>
          ) : (
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3>Create New League</h3>
              <form onSubmit={handleCreateLeague} className="form">
                <div className="form-group">
                  <label htmlFor="leagueName">League Name</label>
                  <input
                    type="text"
                    id="leagueName"
                    value={newLeagueName}
                    onChange={(e) => setNewLeagueName(e.target.value)}
                    placeholder="e.g., Mrs. Smith's Class"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="leagueDescription">Description (optional)</label>
                  <input
                    type="text"
                    id="leagueDescription"
                    value={newLeagueDescription}
                    onChange={(e) => setNewLeagueDescription(e.target.value)}
                    placeholder="e.g., 3rd grade math practice"
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" className="btn btn-primary" disabled={creating}>
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {leagues.length === 0 ? (
          <div className="card">
            <p className="placeholder-text">
              No leagues yet. Create one to get started!
            </p>
          </div>
        ) : (
          <div className="character-grid">
            {leagues.map((league) => (
              <div key={league.id} className="card" style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 style={{ margin: 0 }}>{league.name}</h3>
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: league.role === 'owner' ? '#e3f2fd' : '#f3e5f5',
                    color: league.role === 'owner' ? '#1565c0' : '#7b1fa2',
                    fontWeight: 600,
                  }}>
                    {league.role === 'owner' ? 'Owner' : 'Manager'}
                  </span>
                </div>
                {league.description && (
                  <p className="description">{league.description}</p>
                )}
                <p style={{ fontSize: '14px', color: '#666' }}>
                  {league.player_count} player{league.player_count !== 1 ? 's' : ''}
                </p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/manager/leagues/${league.id}`)}
                  >
                    Manage
                  </button>
                  {league.role === 'owner' && league.player_count === 0 && (
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDeleteLeague(league.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDashboard;
